import { Rental, RentalEvent, RentalStatus } from '../../../services/models/rental';
import { Equipment } from '../../../services/models/assets';

export interface RentalStatusInfo {
  label: string;
  /** Bar background classes for the timeline. */
  barClassName: string;
  /** Badge classes (chip background) for lists / modal. */
  badgeClassName: string;
  /** Status dot color. */
  dotClassName: string;
  /** Solid hex for the multi-lock connector string (softened by group opacity). */
  connectorColor: string;
}

const STATUS_INFO: Record<RentalStatus, RentalStatusInfo> = {
  [RentalStatus.WAITING_PAYMENT]: {
    label: 'Non payé',
    barClassName: 'bg-amber-300/40',
    badgeClassName: 'bg-amber-50 text-amber-700',
    dotClassName: 'bg-amber-400',
    connectorColor: '#FBBF24',
  },
  [RentalStatus.NOT_STARTED]: {
    label: 'Pas commencé',
    barClassName: 'bg-neutral-20',
    badgeClassName: 'bg-neutral-5 text-neutral-70',
    dotClassName: 'bg-neutral-40',
    connectorColor: '#A3A3A3',
  },
  [RentalStatus.RENTED]: {
    label: 'En cours',
    barClassName: 'bg-success-500/25',
    badgeClassName: 'bg-success-100 text-success-600',
    dotClassName: 'bg-success-500',
    connectorColor: '#22C55E',
  },
  [RentalStatus.RETURNED]: {
    label: 'À vérifier',
    barClassName: 'bg-primary-400/35',
    badgeClassName: 'bg-primary-100 text-primary-700',
    dotClassName: 'bg-primary-500',
    connectorColor: '#FF8A4C',
  },
  [RentalStatus.CLOSED]: {
    label: 'Fermée',
    barClassName: 'bg-neutral-10 opacity-30',
    badgeClassName: 'bg-neutral-5 text-neutral-70',
    dotClassName: 'bg-neutral-40',
    connectorColor: '#A3A3A3',
  },
};

const UNKNOWN_STATUS: RentalStatusInfo = {
  label: 'Inconnu',
  barClassName: 'bg-error-100',
  badgeClassName: 'bg-error-100 text-error-600',
  dotClassName: 'bg-error-500',
  connectorColor: '#EF4444',
};

export function getRentalStatusInfo(status: RentalStatus): RentalStatusInfo {
  return STATUS_INFO[status] ?? UNKNOWN_STATUS;
}

// French labels for the audit-trail causes (api RentalEventCause). Unknown
// causes fall back to the raw code so new API causes stay visible.
const EVENT_CAUSE_LABELS: Record<string, string> = {
  creation_booking: 'Réservation créée',
  creation_manual: 'Créée manuellement',
  webhook_payment_succeeded: 'Paiement confirmé',
  cron_payment_settled: 'Paiement validé (auto)',
  cron_unpaid_timeout: 'Fermée automatiquement (non payée)',
  cron_auto_start: 'Commencée (auto)',
  cron_auto_end: 'Terminée (auto)',
  admin_end: 'Terminée par un admin',
  admin_close: 'Fermée par un admin',
  user_end: 'Terminée par le client',
  preauth_released: 'Pré-autorisation libérée',
  extended: 'Prolongée',
  equipment_swapped: 'Équipement Échangé',
};

export function getRentalEventCauseLabel(event: RentalEvent): string {
  return EVENT_CAUSE_LABELS[event.cause] ?? event.cause;
}

export function getRentalEventActorName(event: RentalEvent): string {
  return event.actor
    ? `${event.actor.firstName ?? ''} ${event.actor.lastName ?? ''}`.trim()
    : '';
}

// French labels for the machine codes in the audit trail's `details` column
// (e.g. whether an admin close charged the customer). Unknown codes fall back
// to the raw text so new API details stay visible.
export function getRentalEventDetailLabel(event: RentalEvent): string {
  const details = event.details;
  if (!details) return '';
  if (details.startsWith('charged:')) {
    const cents = Number(details.slice('charged:'.length));
    return Number.isFinite(cents) ? `Facturé ${(cents / 100).toFixed(2)} €` : 'Facturé';
  }
  if (details === 'no_charge') return 'Sans facturation';
  if (details === 'cash') return 'Espèces';
  if (details === 'uncharged_cash') return 'Espèces non encaissées';
  // extendRental's audit details, e.g. "1h+2h,price:2000->6000"
  const extendMatch = details.match(/^\d+h\+(\d+)h,price:(\d+)->(\d+)$/);
  if (extendMatch) {
    const [, addedHours, oldPriceCents, newPriceCents] = extendMatch;
    return `${addedHours}h (${(Number(oldPriceCents) / 100).toFixed(2)} € -> ${(Number(newPriceCents) / 100).toFixed(2)} €)`;
  }
  return details;
}

/** Token of the code opening the lock holding this equipment (active codes first). */
export function getEquipmentCodeToken(rental: Rental, equipment: Equipment): string | null {
  if (!equipment.lockId || !rental.codes?.length) return null;
  const matches = rental.codes.filter((code) => code.locks?.some((lock) => lock.id === equipment.lockId));
  const code = matches.find((c) => !c.revoked) ?? matches[0];
  return code?.token ?? null;
}

/** Human label of the physical lock holding this equipment, e.g. "Lock 3" (position is 0-based). */
export function getEquipmentLockLabel(equipment: Equipment): string | null {
  if (equipment.lock?.position == null) return null;
  return `Lock ${equipment.lock.position + 1}`;
}
